// src/BookingManagement/BikeCalendar.js - Unified Reservations-Based Bike Booking Calendar
import React, { useState, useEffect } from "react";
import { Calendar, ChevronLeft, ChevronRight, Info, User, FileText, MapPin, AlertCircle, CheckCircle, Building, Shield, Bike, Database, Bug, Wrench, Users } from "lucide-react";
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  getDocs,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase';

// Import enhanced AuthContext for multi-tenant support
import { useAuth } from '../contexts/AuthContext';

import { 
  calculateRentalDuration, 
  checkOverdueStatus,
  validateMultiTenantParams,
  checkMultiTenantMigration
} from './bikeUtils';

// Convert reservation to rental format for compatibility
const convertReservationToRental = (reservation) => {
  return {
    // Keep original reservation data
    ...reservation,
    
    // Map reservation fields to rental fields for compatibility
    bike: reservation.motorbikeNumber,
    bikeModel: reservation.motorbikeNumber,
    bikeId: reservation.motorbikeNumber, // For matching purposes
    bikeRegNumber: reservation.motorbikeNumber,
    startDate: reservation.pickupDateTime?.split('T')[0],
    endDate: reservation.dropoffDateTime?.split('T')[0],
    
    // Customer details mapping
    customerDetails: {
      name: reservation.customerName,
      whatsapp: reservation.whatsappNumber,
      localNumber: reservation.localNumber,
      email: reservation.email,
      licenseNumber: reservation.licenseNumber
    },
    
    // Financial details
    totalCost: parseFloat(reservation.rentalAmount) || 0,
    baseCost: parseFloat(reservation.rentalAmount) || 0,
    invoiceNumber: reservation.invoiceNumber || `RES-${reservation.id}`,
    
    // Status mapping
    status: calculateRentalStatusFromReservation(reservation),
    originalReservationStatus: reservation.availabilityStatus,
    rentalStatus: reservation.rentalStatus || 'reserved',
    
    // Metadata
    isFromReservation: true,
    reservationId: reservation.id,
    createdFromReservation: true,
    
    // Locations
    pickupLocation: reservation.pickupLocation || 'office',
    dropoffLocation: reservation.dropoffLocation || 'office',
    customPickupLocation: reservation.customPickupLocation,
    customDropoffLocation: reservation.customDropoffLocation,
    
    // Dates
    createdAt: reservation.createdAt,
    updatedAt: reservation.updatedAt || reservation.createdAt
  };
};

// Calculate rental status from reservation data
const calculateRentalStatusFromReservation = (reservation) => {
  // Handle cancelled/returned reservations
  if (reservation.availabilityStatus === 'cancel') {
    return 'Cancelled';
  }
  
  if (reservation.availabilityStatus === 'returned' || reservation.rentalStatus === 'completed') {
    return 'Returned';
  }

  // Only process booked reservations
  if (reservation.availabilityStatus !== 'booked') {
    return 'Not Active';
  }

  if (!reservation.pickupDateTime || !reservation.dropoffDateTime) {
    return 'Reserved';
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const startDate = new Date(reservation.pickupDateTime);
  startDate.setHours(0, 0, 0, 0);
  
  const endDate = new Date(reservation.dropoffDateTime);
  endDate.setHours(0, 0, 0, 0);

  // Check for manual rental status overrides
  if (reservation.rentalStatus === 'active') {
    return 'Active';
  }

  // Calculate status based on dates
  if (today < startDate) {
    return 'Reserved';
  } else if (today.getTime() === startDate.getTime()) {
    return reservation.rentalStatus === 'active' ? 'Active' : 'Reserved';
  } else if (today > startDate && today <= endDate) {
    return 'Active';
  } else if (today.getTime() === endDate.getTime()) {
    return 'Due Today';
  } else if (today > endDate) {
    return 'Overdue';
  }

  return 'Reserved';
};

// Enhanced bike matching function for reservations
const findMatchingRentalsFromReservations = (selectedBikeObj, allReservations, companyId, allManagedBikes = []) => {
  if (!selectedBikeObj || !allReservations) return [];

  console.log('🔍 UNIFIED: Enhanced bike matching for reservations:', {
    selectedBike: {
      id: selectedBikeObj.id,
      regNumber: selectedBikeObj.regNumber,
      model: selectedBikeObj.model
    },
    totalReservations: allReservations.length,
    companyId
  });

  // Filter for booked reservations only (active rentals)
  const activeReservations = allReservations.filter(reservation => 
    reservation.companyId === companyId && 
    reservation.availabilityStatus === 'booked' &&
    reservation.availabilityStatus !== 'returned' &&
    reservation.availabilityStatus !== 'cancel'
  );
  
  console.log('🏢 UNIFIED: Active reservations (booked):', activeReservations.length);

  const matchingReservations = activeReservations.filter(reservation => {
    const bikeIdentifier = reservation.motorbikeNumber;
    
    if (!bikeIdentifier) return false;

    // Strategy 1: EXACT MATCHES (Highest Priority)
    const exactMatches = [
      bikeIdentifier === selectedBikeObj.regNumber,
      bikeIdentifier === selectedBikeObj.id,
      // Handle combined formats in reservations
      bikeIdentifier === `${selectedBikeObj.regNumber} - ${selectedBikeObj.model}`,
      bikeIdentifier === `${selectedBikeObj.regNumber}-${selectedBikeObj.model}`
    ];

    const hasExactMatch = exactMatches.some(Boolean);

    if (hasExactMatch) {
      console.log('✅ UNIFIED: EXACT match found:', {
        reservationBike: bikeIdentifier,
        selectedBikeReg: selectedBikeObj.regNumber,
        matchType: 'EXACT',
        reservation: {
          id: reservation.id,
          customerName: reservation.customerName,
          pickupDateTime: reservation.pickupDateTime,
          dropoffDateTime: reservation.dropoffDateTime
        }
      });
      return true;
    }

    // Strategy 2: Check if this reservation exactly matches another bike
    const hasExactMatchWithOtherBike = allManagedBikes.some(otherBike => {
      if (otherBike.id === selectedBikeObj.id) return false;
      return bikeIdentifier === otherBike.regNumber || 
             bikeIdentifier === otherBike.id ||
             bikeIdentifier === `${otherBike.regNumber} - ${otherBike.model}`;
    });

    if (hasExactMatchWithOtherBike) {
      console.log('⚠️ UNIFIED: Reservation exactly matches another bike, skipping:', {
        reservationBike: bikeIdentifier,
        selectedBike: selectedBikeObj.regNumber
      });
      return false;
    }

    // Strategy 3: FUZZY MATCHING for legacy data
    if (selectedBikeObj.regNumber && bikeIdentifier) {
      // Extract numbers and compare
      const reservationNumbers = bikeIdentifier.match(/\d+/)?.[0];
      const bikeNumbers = selectedBikeObj.regNumber.match(/\d+/)?.[0];
      
      // Extract letters and compare
      const reservationLetters = bikeIdentifier.match(/[A-Za-z]+/)?.[0]?.toLowerCase();
      const bikeLetters = selectedBikeObj.regNumber.match(/[A-Za-z]+/)?.[0]?.toLowerCase();
      
      if (reservationNumbers && bikeNumbers && reservationNumbers === bikeNumbers &&
          reservationLetters && bikeLetters && reservationLetters === bikeLetters) {
        console.log('✅ UNIFIED: FUZZY match found:', {
          reservationBike: bikeIdentifier,
          selectedBikeReg: selectedBikeObj.regNumber,
          matchType: 'FUZZY',
          numbers: `${reservationNumbers} = ${bikeNumbers}`,
          letters: `${reservationLetters} = ${bikeLetters}`
        });
        return true;
      }
    }

    // Strategy 4: MODEL-BASED matching (lowest priority)
    if (selectedBikeObj.model && bikeIdentifier.toLowerCase().includes(selectedBikeObj.model.toLowerCase())) {
      // Additional safety check
      const couldMatchOtherBike = allManagedBikes.some(otherBike => {
        if (otherBike.id === selectedBikeObj.id) return false;
        return bikeIdentifier.includes(otherBike.regNumber?.split(' ')[0]) || 
               otherBike.regNumber?.includes(bikeIdentifier.split(' ')[0]);
      });

      if (!couldMatchOtherBike) {
        console.log('✅ UNIFIED: MODEL match found:', {
          reservationBike: bikeIdentifier,
          selectedBikeModel: selectedBikeObj.model,
          matchType: 'MODEL_SAFE'
        });
        return true;
      }
    }

    return false;
  });

  console.log(`🎯 UNIFIED: Found ${matchingReservations.length} matching reservations for bike ${selectedBikeObj.regNumber}`);
  
  if (matchingReservations.length === 0) {
    console.log('⚠️ UNIFIED: No matches found. Debug analysis:');
    console.log('Available reservation bike identifiers:', [...new Set(activeReservations.map(r => r.motorbikeNumber))]);
    console.log('Selected bike details:', {
      regNumber: selectedBikeObj.regNumber,
      model: selectedBikeObj.model,
      id: selectedBikeObj.id
    });
  }

  return matchingReservations;
};

// Main Unified BikeCalendar Component
const UnifiedBikeCalendar = ({ 
  activeRentals, 
  setActiveRentals, 
  onRentalsUpdate,
  currentUser,
  currentCompany, 
  getCompanyContext,
  debugMode = false
}) => {
  // Get multi-tenant context
  const auth = useAuth();
  const user = currentUser || auth?.currentUser;
  const company = currentCompany || auth?.currentCompany;
  const getCompanyCtx = getCompanyContext || auth?.getCompanyContext;
  const hasPermission = auth?.hasPermission;

  const companyId = getCompanyCtx ? getCompanyCtx() : null;

  // State Management
  const [bikes, setBikes] = useState([]);
  const [bikeOptions, setBikeOptions] = useState([]);
  const [allReservations, setAllReservations] = useState([]);
  const [selectedBikeModel, setSelectedBikeModel] = useState("");
  const [selectedBike, setSelectedBike] = useState("");
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [bikeRentals, setBikeRentals] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateRentals, setSelectedDateRentals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBikeDetails, setSelectedBikeDetails] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Debug state
  const [debugInfo, setDebugInfo] = useState({});
  const [showDebugPanel, setShowDebugPanel] = useState(debugMode);
  const [matchingAnalysis, setMatchingAnalysis] = useState(null);

  // Firebase collections - using reservations as single source of truth
  const managedBikesCollection = collection(db, 'managedBikes');
  const reservationsCollection = collection(db, `companies/${companyId}/reservations`);

  // Debug function
  const updateDebugInfo = (key, value) => {
    if (debugMode) {
      setDebugInfo(prev => ({
        ...prev,
        [key]: value,
        lastUpdate: new Date().toLocaleTimeString()
      }));
      console.log(`🐛 UNIFIED CALENDAR DEBUG [${key}]:`, value);
    }
  };

  // Permission check
  const checkPermission = (permission) => {
    if (typeof hasPermission === 'function') {
      return hasPermission(permission);
    }
    if (user && user.permissions) {
      return user.permissions.includes(permission);
    }
    if (user && user.role === 'admin') {
      return true;
    }
    console.warn(`⚠️ UnifiedBikeCalendar: Permission check failed for '${permission}' - allowing access by default`);
    return true;
  };

  // Permission check
  if (!checkPermission('bookings')) {
    return (
      <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-orange-400" />
            Unified Booking Calendar
            <span className="ml-2 text-xs bg-red-600 text-white px-2 py-1 rounded-full">
              Access Denied
            </span>
          </h2>
        </div>
        <div className="text-center py-12">
          <Shield className="mx-auto h-20 w-20 text-gray-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">Access Denied</h3>
          <p className="text-gray-500 text-lg">You don't have permission to view the booking calendar.</p>
        </div>
      </div>
    );
  }

  // Company context validation
  if (!companyId || !company) {
    return (
      <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
        <div className="text-center py-12">
          <AlertCircle className="mx-auto h-20 w-20 text-yellow-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">Company Context Missing</h3>
          <p className="text-gray-500 text-lg">Unified calendar requires valid company context.</p>
        </div>
      </div>
    );
  }

  // Load company bikes
  useEffect(() => {
    if (!companyId) return;

    const q = query(managedBikesCollection, where('companyId', '==', companyId));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const updatedBikes = [];
      querySnapshot.forEach((doc) => {
        const bikeData = { id: doc.id, ...doc.data() };
        updatedBikes.push(bikeData);
      });
      
      setBikes(updatedBikes);
      
      const options = updatedBikes.map(bike => ({
        value: bike.id,
        label: `${bike.model} (${bike.regNumber})`,
        bike: bike
      }));
      setBikeOptions(options);
      
      console.log(`🔄 UNIFIED: Real-time bikes update - ${updatedBikes.length} bikes for company ${companyId}`);
      updateDebugInfo('bikesUpdate', {
        count: updatedBikes.length,
        timestamp: new Date().toISOString()
      });
    });

    return () => unsubscribe();
  }, [companyId]);

  // Load reservations (single source of truth)
  useEffect(() => {
    if (!companyId) return;

    console.log(`🎯 UNIFIED CALENDAR: Setting up reservations subscription for company: ${company.name}`);
    
    const q = query(reservationsCollection, orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const updatedReservations = [];
      querySnapshot.forEach((doc) => {
        updatedReservations.push({ id: doc.id, ...doc.data() });
      });
      
      setAllReservations(updatedReservations);
      
      console.log(`🔄 UNIFIED CALENDAR: Real-time reservations update for ${company.name}: ${updatedReservations.length} reservations`);
      
      updateDebugInfo('reservationsUpdate', {
        totalReservations: updatedReservations.length,
        bookedReservations: updatedReservations.filter(r => r.availabilityStatus === 'booked').length,
        timestamp: new Date().toISOString()
      });
    });

    return () => {
      console.log(`🧹 UNIFIED CALENDAR: Cleaning up reservations subscription for ${company.name}`);
      unsubscribe();
    };
  }, [companyId, company]);

  // Initialize component
  useEffect(() => {
    const initializeUnifiedCalendar = async () => {
      if (!companyId || !company) {
        setIsInitialLoading(false);
        return;
      }

      try {
        console.log(`🎯 UNIFIED CALENDAR: Initializing for company ${company.name} (${companyId})`);
        
        if (!validateMultiTenantParams('UnifiedBikeCalendar', companyId, company)) {
          setIsInitialLoading(false);
          return;
        }

        console.log(`✅ UNIFIED CALENDAR: Initialization complete for ${company.name}`);
        
        updateDebugInfo('initialization', {
          completed: true,
          timestamp: new Date().toISOString()
        });
        
      } catch (error) {
        console.error(`❌ UNIFIED CALENDAR: Error initializing for company ${company.name}:`, error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    initializeUnifiedCalendar();
  }, [companyId, company]);

  // Load bike rentals when bike is selected
  useEffect(() => {
    if (selectedBike && companyId) {
      loadBikeRentalsFromReservations(selectedBike);
      loadSelectedBikeDetails(selectedBike);
    } else {
      setBikeRentals([]);
      setSelectedBikeDetails(null);
    }
  }, [selectedBike, allReservations, companyId]);

  // Load selected bike details
  const loadSelectedBikeDetails = (bikeId) => {
    const bikeDetails = bikes.find(bike => bike.id === bikeId);
    
    if (bikeDetails) {
      console.log(`✅ UNIFIED: Found bike details for ${company.name}:`, bikeDetails);
      setSelectedBikeDetails({
        ...bikeDetails,
        isManaged: true,
        companyId: companyId
      });
      updateDebugInfo('selectedBikeDetails', bikeDetails);
    } else {
      setSelectedBikeDetails(null);
    }
  };

  // Load rentals from reservations for selected bike
  const loadBikeRentalsFromReservations = (bikeId) => {
    try {
      setIsLoading(true);
      console.log(`🎯 UNIFIED: Loading rentals from reservations for bike: ${bikeId} (Company: ${company.name})`);
      
      const selectedBikeObj = bikes.find(bike => bike.id === bikeId);
      if (!selectedBikeObj) {
        console.log(`⚠️ UNIFIED: Bike ${bikeId} not found in bikes for ${company.name}`);
        setBikeRentals([]);
        return;
      }
      
      console.log('🚴 UNIFIED: Selected bike object:', selectedBikeObj);
      
      // Find matching reservations using enhanced matching
      const matchingReservations = findMatchingRentalsFromReservations(selectedBikeObj, allReservations, companyId, bikes);
      
      // Convert reservations to rental format for compatibility
      const convertedRentals = matchingReservations.map(convertReservationToRental);
      
      console.log(`✅ UNIFIED: Found ${matchingReservations.length} matching reservations, converted to ${convertedRentals.length} rental format`);
      setBikeRentals(convertedRentals);
      
      // Set matching analysis for debug
      setMatchingAnalysis({
        selectedBike: selectedBikeObj,
        totalReservations: allReservations.length,
        bookedReservations: allReservations.filter(r => r.availabilityStatus === 'booked').length,
        matchingReservations: matchingReservations.length,
        convertedRentals: convertedRentals.length,
        reservationIdentifiers: [...new Set(allReservations.map(r => r.motorbikeNumber))],
        matchingStrategy: matchingReservations.length > 0 ? 'unified_success' : 'no_unified_matches'
      });
      
      updateDebugInfo('bikeRentalsFromReservations', {
        bikeId,
        selectedBike: selectedBikeObj,
        reservationCount: matchingReservations.length,
        rentalCount: convertedRentals.length,
        reservations: matchingReservations,
        rentals: convertedRentals
      });
      
    } catch (error) {
      console.error(`❌ UNIFIED: Error loading bike rentals from reservations for company ${company.name}:`, error);
      setBikeRentals([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Debug functions
  const debugUnifiedMatching = () => {
    console.log('🔍 UNIFIED CALENDAR MATCHING DEBUG:');
    console.log('='.repeat(60));
    
    console.log('🚴 Available bikes:');
    bikes.forEach((bike, index) => {
      console.log(`  ${index}: ${bike.model} - ${bike.regNumber} (ID: ${bike.id})`);
    });
    
    console.log('📊 Reservations with bike identifiers:');
    const bookedReservations = allReservations.filter(r => r.availabilityStatus === 'booked');
    bookedReservations.slice(0, 10).forEach((reservation, index) => {
      console.log(`  ${index}: Bike: "${reservation.motorbikeNumber}", Customer: ${reservation.customerName}, Status: ${reservation.availabilityStatus}`);
    });
    
    if (selectedBike) {
      const selectedBikeObj = bikes.find(bike => bike.id === selectedBike);
      console.log('🎯 Currently selected bike:', selectedBikeObj);
      
      if (selectedBikeObj) {
        console.log('🧪 Testing unified matching for each bike:');
        bikes.forEach((testBike, index) => {
          const testMatches = findMatchingRentalsFromReservations(testBike, allReservations, companyId, bikes);
          console.log(`  ${index}. ${testBike.regNumber} (${testBike.model}): ${testMatches.length} reservation matches`);
          if (testMatches.length > 0) {
            console.log(`    └─ Reservations: ${testMatches.map(r => r.motorbikeNumber).join(', ')}`);
          }
        });
      }
    }
    
    console.log('='.repeat(60));
  };

  // Calendar helper functions
  const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();
  const today = new Date();

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Navigation functions
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
    setSelectedDate(null);
    setSelectedDateRentals([]);
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
    setSelectedDate(null);
    setSelectedDateRentals([]);
  };

  const goToToday = () => {
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    setSelectedDate(null);
    setSelectedDateRentals([]);
  };

  // Get rentals for a specific date
  const getDateRentals = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    const dayRentals = bikeRentals.filter(rental => {
      if (!rental.startDate || !rental.endDate) return false;
      if (rental.status === 'Cancelled' || rental.status === 'Returned') return false;
      
      const startDate = new Date(rental.startDate);
      const endDate = new Date(rental.endDate);
      const checkDate = new Date(dateStr);
      
      return checkDate >= startDate && checkDate <= endDate;
    });
    
    return dayRentals;
  };

  // Get rental status for a date
  const getDateStatus = (day) => {
    const rentals = getDateRentals(day);
    if (rentals.length === 0) return null;
    
    // Priority: Overdue > Active > Reserved > Due Today
    if (rentals.some(r => checkOverdueStatus(r).isOverdue)) return 'overdue';
    if (rentals.some(r => r.status === 'Active')) return 'active';
    if (rentals.some(r => r.status === 'Reserved')) return 'reserved';
    return 'booked';
  };

  // Handle date click
  const handleDateClick = (day) => {
    const dateRentals = getDateRentals(day);
    if (dateRentals.length > 0) {
      setSelectedDate(day);
      setSelectedDateRentals(dateRentals);
      
      updateDebugInfo('dateClick', {
        day,
        rentalsCount: dateRentals.length,
        rentals: dateRentals
      });
    }
  };

  // Get status color classes
  const getStatusColor = (status) => {
    switch (status) {
      case 'overdue':
        return 'bg-red-600 text-white';
      case 'active':
        return 'bg-green-600 text-white';
      case 'reserved':
        return 'bg-indigo-600 text-white';
      case 'booked':
        return 'bg-amber-600 text-white';
      default:
        return 'bg-gray-600 text-gray-300';
    }
  };

  // Render calendar days
  const renderCalendarDays = () => {
    const days = [];
    const totalDays = daysInMonth(currentMonth, currentYear);
    const startDay = firstDayOfMonth(currentMonth, currentYear);

    // Empty cells for days before the first day of the month
    for (let i = 0; i < startDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-12"></div>
      );
    }

    // Calendar days
    for (let day = 1; day <= totalDays; day++) {
      const isToday = today.getDate() === day && 
                     today.getMonth() === currentMonth && 
                     today.getFullYear() === currentYear;
      
      const status = getDateStatus(day);
      const hasRentals = status !== null;
      const rentalsCount = getDateRentals(day).length;
      
      days.push(
        <div
          key={day}
          className={`h-12 border border-gray-600 flex items-center justify-center relative cursor-pointer transition-colors ${
            isToday ? 'bg-orange-600 text-white font-bold' : 
            hasRentals ? getStatusColor(status) : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
          onClick={() => handleDateClick(day)}
        >
          <span className="text-sm">
            {day}
          </span>
          {hasRentals && rentalsCount > 1 && (
            <span className="absolute top-0 right-0 bg-white text-gray-800 text-xs rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold">
              {rentalsCount}
            </span>
          )}
        </div>
      );
    }

    return days;
  };

  // Debug Panel Component
  const DebugPanel = () => {
    if (!showDebugPanel) return null;

    return (
      <div className="mb-6 bg-purple-900/20 border border-purple-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-purple-300 font-medium flex items-center gap-2">
            <Bug size={16} />
            Unified Calendar Debug Panel
            <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded-full">
              Reservations-Based
            </span>
          </h4>
          <button
            onClick={() => setShowDebugPanel(false)}
            className="text-purple-400 hover:text-purple-300"
          >
            Hide
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm mb-4">
          <div className="bg-gray-800/50 rounded p-3">
            <div className="text-purple-300 font-medium mb-1">Bikes</div>
            <div className="text-gray-300">Total: {bikes.length}</div>
            <div className="text-gray-400 text-xs">Selected: {selectedBike || 'None'}</div>
          </div>
          <div className="bg-gray-800/50 rounded p-3">
            <div className="text-purple-300 font-medium mb-1">Reservations</div>
            <div className="text-gray-300">Total: {allReservations.length}</div>
            <div className="text-gray-400 text-xs">Booked: {allReservations.filter(r => r.availabilityStatus === 'booked').length}</div>
          </div>
          <div className="bg-gray-800/50 rounded p-3">
            <div className="text-purple-300 font-medium mb-1">Calendar Rentals</div>
            <div className="text-gray-300">For bike: {bikeRentals.length}</div>
            <div className="text-gray-400 text-xs">From reservations</div>
          </div>
          <div className="bg-gray-800/50 rounded p-3">
            <div className="text-purple-300 font-medium mb-1">Matching</div>
            <div className="text-gray-300">
              {matchingAnalysis ? matchingAnalysis.matchingStrategy : 'Not analyzed'}
            </div>
            <div className="text-gray-400 text-xs">Unified algorithm</div>
          </div>
        </div>

        {matchingAnalysis && (
          <div className="mb-4 bg-gray-900/50 rounded p-3">
            <div className="text-green-300 font-medium mb-2">Unified Matching Analysis</div>
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <div className="text-gray-400">Selected Bike:</div>
                <div className="text-gray-300">
                  {matchingAnalysis.selectedBike?.model} ({matchingAnalysis.selectedBike?.regNumber})
                </div>
              </div>
              <div>
                <div className="text-gray-400">Matches Found:</div>
                <div className="text-gray-300">
                  {matchingAnalysis.matchingReservations} reservations → {matchingAnalysis.convertedRentals} rentals
                </div>
              </div>
            </div>
            <div className="mt-2">
              <div className="text-gray-400 text-xs">Reservation Bike Identifiers:</div>
              <div className="text-gray-300 text-xs">
                {matchingAnalysis.reservationIdentifiers.filter(Boolean).slice(0, 5).join(', ')}
                {matchingAnalysis.reservationIdentifiers.length > 5 && '...'}
              </div>
            </div>
          </div>
        )}
        
        <div className="flex gap-2">
          <button
            onClick={debugUnifiedMatching}
            className="bg-purple-600 text-white px-3 py-1 rounded text-xs"
          >
            Debug Matching
          </button>
          <button
            onClick={() => console.log('Unified Debug Info:', debugInfo)}
            className="bg-blue-600 text-white px-3 py-1 rounded text-xs"
          >
            Log Debug Info
          </button>
        </div>
      </div>
    );
  };

  // Helper function to get unique bike models
  const getUniqueBikeModels = () => {
    const models = bikes.map(bike => bike.model).filter(Boolean);
    const uniqueModels = [...new Set(models)];
    return uniqueModels.sort();
  };

  // Helper function to filter bikes by selected model
  const getFilteredBikes = () => {
    if (!selectedBikeModel) return [];
    return bikes.filter(bike => bike.model === selectedBikeModel);
  };

  // Handler for bike model change
  const handleBikeModelChange = (model) => {
    setSelectedBikeModel(model);
    setSelectedBike(""); // Reset bike selection when model changes
    setSelectedBikeDetails(null);
    setBikeRentals([]);
  };

  if (isInitialLoading) {
    return (
      <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-orange-400" />
            Unified Booking Calendar
            <span className="ml-2 text-xs bg-purple-600 text-white px-2 py-1 rounded-full">
              Reservations-Based
            </span>
          </h2>
        </div>
        
        <div className="p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mr-3"></div>
            <div>
              <h3 className="text-white font-medium">Loading Unified Calendar</h3>
              <p className="text-gray-400 text-sm">Single source of truth: Reservations for {company?.name}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
      <div className="p-6 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-orange-400" />
              Unified Booking Calendar
              <span className="ml-2 text-xs bg-purple-600 text-white px-2 py-1 rounded-full">
                Reservations-Based
              </span>
              {debugMode && (
                <span className="ml-2 text-xs bg-yellow-600 text-white px-2 py-1 rounded-full">
                  DEBUG
                </span>
              )}
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Single source of truth: Reservations collection with enhanced matching
            </p>
            <div className="mt-2 flex items-center text-blue-400 text-sm">
              <Building size={16} className="mr-1" />
              <span>{company.name} • Unified Architecture</span>
            </div>
          </div>
          <div className="flex gap-2">
            {debugMode && (
              <button
                onClick={() => setShowDebugPanel(!showDebugPanel)}
                className="bg-purple-600 text-white px-3 py-1 rounded text-sm"
              >
                {showDebugPanel ? 'Hide' : 'Show'} Debug
              </button>
            )}
            <button
              onClick={debugUnifiedMatching}
              className="bg-blue-600 text-white px-3 py-1 rounded text-sm flex items-center gap-1"
            >
              <Wrench size={14} />
              Analyze
            </button>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        <DebugPanel />
        
        {/* Unified System Info Panel */}
        <div className="mb-6 bg-purple-900/30 border border-purple-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Shield className="text-purple-400 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <h3 className="text-purple-300 font-medium mb-2 flex items-center gap-2">
                <Users size={16} />
                Unified Calendar Architecture: {company.name}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-purple-200">Data Source:</span>
                  <div className="text-gray-300">✅ Reservations Only</div>
                </div>
                <div>
                  <span className="text-purple-200">Bikes Available:</span>
                  <div className="text-gray-300">{bikes.length} managed bikes</div>
                </div>
                <div>
                  <span className="text-purple-200">Total Reservations:</span>
                  <div className="text-gray-300">{allReservations.length} records</div>
                </div>
                <div>
                  <span className="text-purple-200">Active Bookings:</span>
                  <div className="text-purple-300">{allReservations.filter(r => r.availabilityStatus === 'booked').length} booked</div>
                </div>
              </div>
              <div className="mt-2 p-2 bg-green-900/20 border border-green-700 rounded text-xs">
                <p className="text-green-300">
                  <strong>✅ Unified Architecture:</strong> Calendar now uses reservations as single source of truth. 
                  When bikes return → reservations update → calendar automatically reflects changes. No sync issues.
                </p>
              </div>
            </div>
          </div>
        </div>

        {bikes.length === 0 ? (
          <div className="text-center py-12">
            <Bike className="mx-auto h-20 w-20 text-gray-400 mb-6" />
            <h3 className="text-2xl font-semibold text-gray-300 mb-4">No Bikes Found</h3>
            <p className="text-gray-500 text-lg">No bikes found in {company.name}'s bike management system.</p>
          </div>
        ) : (
          <>
            {/* Bike Selection - Step 1: Model */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-gray-300">
                Step 1: Select Bike Model from {company.name}
              </label>
              <select 
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white appearance-none"
                value={selectedBikeModel}
                onChange={(e) => handleBikeModelChange(e.target.value)}
              >
                <option value="">Choose a bike model...</option>
                {getUniqueBikeModels().map(model => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>

            {/* Bike Selection - Step 2: Bike Number */}
            {selectedBikeModel && (
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 text-gray-300">
                  Step 2: Select Bike Number
                </label>
                <select 
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white appearance-none"
                  value={selectedBike}
                  onChange={(e) => {
                    setSelectedBike(e.target.value);
                    updateDebugInfo('bikeSelection', e.target.value);
                  }}
                >
                  <option value="">Choose a bike number...</option>
                  {getFilteredBikes().map(bike => (
                    <option key={bike.id} value={bike.id}>
                      {bike.regNumber} {bike.model && `- ${bike.model}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedBike && (
              <>
                {/* Calendar Header */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={goToPreviousMonth}
                        className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <h3 className="text-xl font-semibold text-white">
                        {monthNames[currentMonth]} {currentYear}
                      </h3>
                      <button
                        onClick={goToNextMonth}
                        className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                    <button
                      onClick={goToToday}
                      className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm transition-colors"
                    >
                      Today
                    </button>
                  </div>

                  {/* Legend */}
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-gray-600 rounded"></div>
                      <span className="text-gray-300">Available</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-indigo-600 rounded"></div>
                      <span className="text-gray-300">Reserved</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-green-600 rounded"></div>
                      <span className="text-gray-300">Active</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-red-600 rounded"></div>
                      <span className="text-gray-300">Overdue</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-orange-600 rounded"></div>
                      <span className="text-gray-300">Today</span>
                    </div>
                  </div>
                </div>

                {/* Calendar Grid */}
                <div className="mb-6">
                  {/* Day headers */}
                  <div className="grid grid-cols-7 mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="h-8 bg-gray-900 flex items-center justify-center text-sm font-medium text-gray-300 border border-gray-600">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar days */}
                  <div className="grid grid-cols-7">
                    {renderCalendarDays()}
                  </div>
                </div>

                {/* Bike Info */}
                <div className="bg-gray-700 rounded-lg p-4 mb-6">
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    Selected Motorcycle Details
                    <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded-full">
                      Unified Data
                    </span>
                  </h4>
                  
                  {selectedBikeDetails ? (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-400">Model:</span>
                          <p className="text-white font-medium">{selectedBikeDetails.model}</p>
                        </div>
                        <div>
                          <span className="text-gray-400">Registration:</span>
                          <p className="text-white font-medium">{selectedBikeDetails.regNumber}</p>
                        </div>
                        <div>
                          <span className="text-gray-400">Daily Rate:</span>
                          <p className="text-white font-medium">
                            ${selectedBikeDetails.dailyRate}
                            <span className="text-purple-400 text-xs ml-1">(Unified)</span>
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-400">Bookings:</span>
                          <p className="text-white font-medium">
                            {bikeRentals.length}
                            <span className="text-purple-400 text-xs ml-1">(From Reservations)</span>
                          </p>
                        </div>
                      </div>

                      {bikeRentals.length > 0 && (
                        <div className="mt-4 p-3 bg-purple-900/20 border border-purple-700 rounded">
                          <div className="text-purple-300 text-sm font-medium mb-1">Unified Data Results</div>
                          <div className="text-purple-200 text-xs">
                            Found {bikeRentals.length} active booking(s) from reservations data
                          </div>
                          <div className="text-purple-400 text-xs mt-1">
                            Source: Reservations collection (single source of truth)
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto mb-2"></div>
                      <p className="text-gray-400 text-sm">Loading bike details...</p>
                    </div>
                  )}
                </div>

                {/* Selected Date Details */}
                {selectedDate && selectedDateRentals.length > 0 && (
                  <div className="bg-gray-700 rounded-lg p-4 mb-6">
                    <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      Bookings on {monthNames[currentMonth]} {selectedDate}, {currentYear}
                      <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded-full">
                        From Reservations
                      </span>
                    </h4>
                    
                    <div className="space-y-4">
                      {selectedDateRentals.map((rental, index) => {
                        const { isOverdue, daysOverdue } = checkOverdueStatus(rental);
                        
                        return (
                          <div key={index} className="bg-gray-600 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <h5 className="text-blue-400 text-sm font-medium mb-2 flex items-center">
                                  <User className="w-4 h-4 mr-1" /> Customer Information
                                </h5>
                                <div className="space-y-1 text-sm">
                                  <div>
                                    <span className="text-gray-400">Name:</span>
                                    <p className="text-white">{rental.customerDetails?.name || "Unknown"}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-400">Contact:</span>
                                    <p className="text-white">
                                      {rental.customerDetails?.whatsapp || rental.customerDetails?.localNumber || "N/A"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              
                              <div>
                                <h5 className="text-green-400 text-sm font-medium mb-2 flex items-center">
                                  <FileText className="w-4 h-4 mr-1" /> Booking Information
                                </h5>
                                <div className="space-y-1 text-sm">
                                  <div>
                                    <span className="text-gray-400">Period:</span>
                                    <p className="text-white">
                                      {rental.startDate ? new Date(rental.startDate).toLocaleDateString() : "?"} - 
                                      {rental.endDate ? new Date(rental.endDate).toLocaleDateString() : "?"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-gray-400">Duration:</span>
                                    <p className="text-white">
                                      {rental.startDate && rental.endDate 
                                        ? `${calculateRentalDuration(rental.startDate, rental.endDate)} days` 
                                        : "Unknown"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              
                              <div>
                                <h5 className="text-purple-400 text-sm font-medium mb-2 flex items-center">
                                  <FileText className="w-4 h-4 mr-1" /> Status & Cost
                                </h5>
                                <div className="space-y-1 text-sm">
                                  <div>
                                    <span className="text-gray-400">Status:</span>
                                    <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                                      isOverdue ? 'bg-red-900 text-red-400' : 'bg-gray-800 text-gray-300'
                                    }`}>
                                      {isOverdue ? `Overdue (${daysOverdue}d)` : rental.status}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-400">Cost:</span>
                                    <p className="text-white">${rental.totalCost || '0'}</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="mt-3 p-2 bg-purple-900/20 border border-purple-700 rounded text-center">
                              <div className="text-xs text-purple-300 flex items-center justify-center">
                                <Users size={12} className="mr-1" />
                                Unified Data: {company.name}
                                <span className="ml-2 text-purple-400">
                                  Reservation ID: {rental.reservationId}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <button 
                      onClick={() => {
                        setSelectedDate(null);
                        setSelectedDateRentals([]);
                      }}
                      className="mt-4 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm"
                    >
                      Close Details
                    </button>
                  </div>
                )}

                {/* No rentals message */}
                {bikeRentals.length === 0 && !isLoading && selectedBikeDetails && (
                  <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 text-center">
                    <CheckCircle className="w-10 h-10 mx-auto mb-3 text-green-400" />
                    <p className="text-green-200 font-medium">No bookings found for this motorcycle</p>
                    <p className="text-gray-300 mt-2">This motorcycle is completely available</p>
                    <div className="mt-3 p-2 bg-purple-900/20 border border-purple-700 rounded-lg">
                      <p className="text-xs text-purple-300 flex items-center justify-center">
                        <Users size={14} className="mr-1" />
                        Unified search completed against {company.name} reservations
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* No bike selected message */}
            {!selectedBike && (
              <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 text-center">
                <Calendar className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                <p className="text-gray-200 font-medium">Select a motorcycle to view its unified booking calendar</p>
                <p className="text-gray-400 mt-2">Single source of truth: Reservations collection for {company.name}</p>
                <div className="mt-3 p-2 bg-purple-900/20 border border-purple-700 rounded-lg">
                  <p className="text-xs text-purple-300">
                    ✨ Unified architecture eliminates sync issues between systems
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Debug info for development */}
        {debugMode && (
          <div className="mt-6 p-3 bg-purple-900/20 border border-purple-700 rounded-lg text-xs">
            <div className="text-purple-300 mb-1">🎯 Unified Calendar Debug Info:</div>
            <div className="text-purple-200">
              Company: {company?.name} ({companyId}) | 
              Bikes: {bikes.length} | 
              Reservations: {allReservations.length} | 
              Active: {allReservations.filter(r => r.availabilityStatus === 'booked').length}
            </div>
            <div className="mt-2 text-purple-100">
              Selected: {selectedBike} | Calendar Rentals: {bikeRentals.length} | Unified Architecture: Active
            </div>
          </div>
        )}
      </div>
      
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40">
          <div className="bg-gray-800 rounded-lg p-6 flex items-center border border-gray-700">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mr-3"></div>
            <span className="text-white">Loading unified calendar data for {company.name}...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedBikeCalendar;